export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export interface DayHours { closed: boolean; opening: string; closing: string; hasBreak?: boolean; breakStart?: string; breakEnd?: string; }
export type WeeklyHours = Record<DayKey, DayHours>;

export const DAY_KEYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export const DAY_LABELS: Record<DayKey, string> = { mon: 'Lundi', tue: 'Mardi', wed: 'Mercredi', thu: 'Jeudi', fri: 'Vendredi', sat: 'Samedi', sun: 'Dimanche' };
// Date.getDay() : 0=dimanche ... 6=samedi
export const JS_DAY_TO_KEY: DayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export const defaultWeeklyHours = (): WeeklyHours => {
    const hours = {} as WeeklyHours;
    DAY_KEYS.forEach((k) => { hours[k] = { closed: false, opening: '08:00', closing: '20:00' }; });
    return hours;
};

export const PX_PER_HOUR = 90;

export const timeToMinutes = (t: string) => {
    const [h, m] = String(t || '0:0').split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
};

export const minutesToTime = (mins: number) => {
    const h = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const formatDateInput = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export const formatDateHuman = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

export const contactLabel = (a: { crm_first_name?: string; crm_last_name?: string; crm_company_name?: string }) => {
    if (a.crm_company_name) return a.crm_company_name;
    return [a.crm_first_name, a.crm_last_name].filter(Boolean).join(' ') || 'Client';
};

export const dayKeyForDate = (dateStr: string): DayKey => JS_DAY_TO_KEY[new Date(dateStr + 'T00:00:00').getDay()];

export const dayShortLabel = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return `${DAY_LABELS[dayKeyForDate(dateStr)].slice(0, 3)} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export interface EmailLogEntry {
    id: string;
    email_type: string;
    subject: string;
    recipient_email: string;
    sent_at: string;
    html_body?: string;
}

export const EMAIL_TYPE_LABELS: Record<string, string> = {
    confirmation: 'Confirmation de rendez-vous',
    reminder: 'Rappel avant rendez-vous',
    invoice_completion: 'Facture de fin de prestation',
    payment_confirmation: 'Confirmation de paiement',
    custom: 'Message personnalisé',
};

export interface PackedAppointment<T> { appt: T; lane: number; }

export const packAppointments = <T extends { start_time: string; computed_duration_minutes: number }>(appts: T[]): { packed: PackedAppointment<T>[]; laneCount: number } => {
    const sorted = [...appts].sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
    const laneEndTimes: number[] = [];
    const packed: PackedAppointment<T>[] = [];
    sorted.forEach((appt) => {
        const start = timeToMinutes(appt.start_time);
        const end = start + Math.max(1, appt.computed_duration_minutes);
        let lane = laneEndTimes.findIndex((endTime) => endTime <= start);
        if (lane === -1) { laneEndTimes.push(end); lane = laneEndTimes.length - 1; }
        else laneEndTimes[lane] = end;
        packed.push({ appt, lane });
    });
    return { packed, laneCount: Math.max(1, laneEndTimes.length) };
};
